<template>
<div class="container-fluid">
	<contact-toolbar :heading="$store.contact.name+' Assets'" :sub-heading="$store.contact.address">
		<search-bar 
			placeholder="Search assets"
			v-model="searchTerms"
			v-on:submit="searchAssets"
			v-on:clearSearch="showingSearchResults = false">
		</search-bar>
		<template slot="main">
			<div>
				<context-menu
					:menu-items="contextMenuItems"
					v-on:editCat="editCat"
					v-on:newCategory="newCategory"
					v-on:deleteCategory="deleteCategory"
					v-on:newAsset="newAsset"
					v-on:openCategoryManager="openCategoryManager">
				</context-menu>
			</div>
		</template>
	</contact-toolbar>

	<field 
		class="mb-1"
		:params="{
			type:'boolean',
			doubleLabels:['By Category','By Location'],
		}"
		v-model="showByLocation"
		v-on:change="showByChange">
	</field>

	<template v-if="!showByLocation && !categorySelected && !showingSearchResults">
		<h4>Categories</h4>

		<div v-if="!categorySelected " class="list-group">
			<button v-for="cat in filteredCategories" 
				class="list-group-item list-group-item-action"
				type="button" 
				:key="cat.id+'_'+cat.name"
				v-on:click="selectCat(cat)">
				{{cat.name}}
			</button>
			<p v-if="filteredCategories.length == 0">You have no active categories. <a href="#" v-on:click.prevent="openCategoryManager">Open Category Manager</a></p>
		</div>
	</template>
	<template v-else-if="showByLocation && !locationSelected && !showingSearchResults">
		<h4>Locations</h4>

		<div v-if="!locationSelected" class="list-group">
			<button v-for="location in locations" 
				class="list-group-item list-group-item-action"
				type="button" 
				:key="location.id+'_'+location.location_name"
				v-on:click="selectLocation(location)">
				{{location.location_name}}
			</button>
		</div>
	</template>
	<div v-else>

		<ol v-if="locationSelected && showByLocation" class="breadcrumb">

			<li class="breadcrumb-item"><a href="#"><i class="fa fa-arrow-up" v-on:click="backToLocations"></i></a></li>

			<li class="breadcrumb-item">
				<a href="#" v-on:click.prevent="backToLocations">
					Locations
				</a>
			</li>

			<template v-if="locationSelected">
				<li class="breadcrumb-item">
					{{locationSelected.location_name}}
				</li>
			</template>
		</ol>
		<ol v-else-if="categorySelected && !showByLocation" class="breadcrumb">

			<li class="breadcrumb-item"><a href="#"><i class="fa fa-arrow-up" v-on:click="backToCats"></i></a></li>

			<li class="breadcrumb-item">
				<a href="#" v-on:click.prevent="backToCats">
					Categories
				</a>
			</li>

			<template v-if="categorySelected">
				<li class="breadcrumb-item">
					{{categorySelected.name}}
				</li>
			</template>
		</ol>

		<table-list record-name="Asset"
			:records="mutatedAssets"
			:fields="[
				{label: 'Description',key:'description'},
				{label: 'Serial #',key: 'serial_num'},
				{label: 'Customer Location',key: 'customerLocationName'},
				{label: 'Next Service',key:'next_service_date'}
			]"
			has-edit="/contact/asset/{?}/log"
			id-field="slug"
			:clickable-row="true"
			:hide-record-count="true"
			:sortable="true"
			edit-button-text="Open"
			:loading="assetsLoading">

		</table-list>

		<div class="text-center" v-if="mutatedAssets.length > 0 && numPages > page">
			<button class="btn btn-light" type="button" v-on:click="loadMore">
				Load More
			</button>
		</div>
	</div>

	<modal-dialog :modal-visible="addCategoryModal.visible"
		title="Add an Asset Category"
		confirm-button-text="Save"
		v-on:closeModal="addCategoryModal.visible = false"
		v-on:confirm="saveCategory">

		<field label="Category Name"
			col-class="col-md-4"
			v-model="addCategoryModal.name">
		</field>

	</modal-dialog>

	<modal-dialog :modal-visible="selectCategoryModal.visible"
		title="Select Active Categories"
		confirm-button-text="Save"
		:working="selectCategoryModal.working"
		v-on:closeModal="selectCategoryModal.visible = false"
		v-on:confirm="saveActiveCategories">

		Select which categories should be active for this customer

		<field label="Active Categories"
			col-class="stacked"
			:params="{type:'select-multi',options: categoriesOptions}"
			v-model="selectCategoryModal.activeCategories">
		</field>

	</modal-dialog>

	<modal-dialog :modal-visible="deleteModal.visible"
		title="Delete Category"
		confirm-button-text="Yes, Delete"
		button-class="btn-outline-danger"
		v-on:closeModal="deleteModal.visible = false"
		v-on:confirm="confirmDelete">

		Are you sure you want to delete this category?

		</modal-dialog>
</div>
</template>

<script>
import ContactToolbar from "./ContactToolbar"
import ContextMenu from "@/layout_fragments/ContextMenu"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"
import Field from "@/components/Field"
import SearchBar from "@/components/SearchBar"

import ContactMixins from "@/mixins/ContactMixins"

export default {
	components: {
		ContactToolbar,
		TableList,
		ContextMenu,
		ModalDialog,
		Field,
		SearchBar
	},
	mixins: [ContactMixins],
	data() {
		return  {
			showByLocation: false,
			categorySelected: null,
			categories: [],
			activeCategories: [],
			locationSelected: false,
			assets: [],
			page: 1,
			numPages: 0,
			numResults: 0,
			locations: [],
			assetsLoading: false,
			addCategoryModal: {
				visible: false,
				name: null,
				id: null
			},
			deleteModal: {
				visible: false
			},
			selectCategoryModal: {
				visible: false,
				working: false,
				activeCategories: []
			},
			setLastRoute: true,
			searchTerms: "",
			showingSearchResults: false
		}
	},
	computed: {
		categoriesOptions() {
			return this.optionify(this.categories,"name")
		},
		filteredCategories() {
			return this.categories.filter(cat => this.activeCategories.indexOf(cat.id) >= 0)
		},
		contextMenuItems() {
			let items = []

			if (this.categorySelected) {

				items.push({
					text: 'Add Asset',
					icon:'fa-plus',
					clickFunction:'newAsset'
				})

				items.push({
					text: 'Edit Category',
					icon:'fa-pencil-alt',
					clickFunction:'editCat'
				})

				let deleteDisabled = true
				if (this.assets.length == 0)
					deleteDisabled = false

				items.push({
					text: 'Delete Category',
					icon:'fa-trash',
					clickFunction:'deleteCategory',
					itemClass:'text-danger',
					disabled: deleteDisabled
				})
			}
			else {
				items.push({text: 'Add Category',icon: 'fa-plus',clickFunction:'newCategory'})
			}

			items.push({
				text: 'Active Categories',
				icon:'fa-tags',
				clickFunction:'openCategoryManager'
			})

			return items
		},
		mutatedAssets() {
			return this.assets.map(asset => {

				asset.customerLocationName = "Main"
				if ("ContactLocation" in asset && asset.ContactLocation) {
					let location = asset.ContactLocation

					if (location.location_name)
						asset.customerLocationName = location.location_name
					else
						asset.customerLocationName = location.suite + " " + location.address
				}

				return asset
			})
		}
	},
	methods: {
		backToCats() {
			this.categorySelected = false
			this.page = 1
			this.assets = []
			window.history.pushState({},"","/contact/"+this.$store.contact.slug+"/assets")
		},
		backToLocations() {
			this.locationSelected = false
			this.page = 1
			this.assets = []
			window.history.pushState({},"","/contact/"+this.$store.contact.slug+"/assets")
		},
		selectCat(cat) {
			let vm = this

			this.page = 1
			this.assets = []
			
			vm.fetchAssets(cat.id).then(response => {
				vm.categorySelected = cat
				let route = "/contact/"+vm.$store.contact.slug+"/assets/"+cat.name.slugify()
				window.history.pushState({},"",route)
				vm.setLastRoute = false
				vm.$store.lastRoute = route
			})
		},
		selectLocation(location) {
			let vm = this

			this.page = 1
			this.assets = []

			vm.fetchAssets(location.id, "location_id").then(response => {
				vm.locationSelected = location
				/*
				let route = "/contact/"+vm.$store.contact.slug+"/assets/"+cat.name.slugify()
				window.history.pushState({},"",route)
				vm.setLastRoute = false
				vm.$store.lastRoute = route
				*/
			})
		},
		showByChange(showByLocation) {
			let vm = this

			if (showByLocation)
				this.fetchLocations(true)
			else
				this.fetchCategories(true)
		},
		deleteCategory() {
			this.deleteModal.visible = true
			this.deleteModal.recordType = "Category"
		},
		confirmDelete() {
			let vm = this

			this.postData("CustomerAssetCategory/delete",{id:vm.categorySelected.id},true).then(response => {
				if (response.success) {
					vm.alertSuccess("Category deleted")

					vm.deleteModal.visible = false
					vm.backToCats()
					vm.fetchCategories()
				}
			})
		},
		newCategory() {
			this.addCategoryModal.visible = true
			this.addCategoryModal.name = ""
		},
		editCat() {
			this.addCategoryModal.visible = true
			this.addCategoryModal.name = this.catSelected.name
			this.addCategoryModal.id = this.catSelected.id
		},
		saveCategory() {
			let vm = this

			let payload = {
				name : this.addCategoryModal.name
			}

			if (this.addCategoryModal.id)
				payload.id = this.addCategoryModal.id

			this.postData("CustomerAssetCategory/save",payload).then(response => {
				if (response.success) {
					vm.alertSaved("Category added")
					vm.addCategoryModal.visible = false
					vm.fetchCategories()
				}
			})
		},
		newAsset() {
			this.$router.push("/contact/asset/new/"+this.$store.contact.id+"/0/"+this.categorySelected.id)
		},
		openCategoryManager() {
			this.selectCategoryModal.activeCategories = this.cloneObj(this.activeCategories)
			this.selectCategoryModal.visible = true
		},
		saveActiveCategories() {
			let vm = this

			let payload = {
				id: this.$store.contact.id,
				CustomerAssetCategory: this.selectCategoryModal.activeCategories
			}

			this.selectCategoryModal.working = true

			this.postData("Contact/save",payload,true).then(response => {
				vm.selectCategoryModal.working = false
				vm.selectCategoryModal.visible = false

				vm.activeCategories = vm.cloneObj(vm.selectCategoryModal.activeCategories)
			})
		},
		loadMore() {
			this.page++

			if (this.showByLocation) 
				this.fetchAssets(this.locationSelected.id,"location_id")
			else
				this.fetchAssets(this.categorySelected.id)
		},
		searchAssets(terms) {
			let vm = this

			return new Promise((resolve,reject) => {

				let params = {
					filters: [
						["description",terms,"and","like"],
						["serial_num",terms,"or","like"]
					],
					include: [
						"ContactLocation"
					]
				}

				vm.spinner(true)

				vm.assetsLoading = true
				vm.showByLocation = false

				vm.getJSON("CustomerAsset/get",params).then(response => {
					vm.spinner(false)
					vm.assetsLoading = false

					if (response.success) {
						vm.assets = response.data
						vm.showingSearchResults = true
					}

					resolve(response)
				})
			})
		},
		fetchAssets(parentId,parentKey) {
			let vm = this

			if (!parentKey)
				parentKey = "category_id"

			return new Promise((resolve,reject) => {

				let params = {
					filters: [
						["contact_id",vm.$store.contact.id]
					],
					include: [
						"ContactLocation"
					],
					page: this.page
				}

				if (parentId == 'main') 
					params.filters.push(['location_id',0])
				else
					params.filters.push([parentKey,parentId])
				
				vm.spinner(true)

				if (vm.page == 1)
					vm.assetsLoading = true

				vm.getJSON("CustomerAsset/get",params).then(response => {
					vm.spinner(false)
					vm.assetsLoading = false

					if (response.success) {
						vm.assets = [...vm.assets,...response.data]
						vm.numPages = response.meta.num_pages
						vm.numResults = response.meta.num_results
					}

					resolve(response)
				})
			})
		},
		fetchCategories(spinner) {
			let vm = this

			if (spinner)
				this.spinner(true)

			return new Promise((resolve,reject) => {

				vm.getJSON("CustomerAssetCategory/get").then(response => {
					if (spinner)
						vm.spinner(false)

					if (response.success) {
						vm.categories = response.data
					}

					resolve(response)
				})
			})
		},
		fetchLocations(spinner) {
			let vm = this

			if (spinner)
				this.spinner(true)

			return new Promise((resolve,reject) => {

				let params = {
					filters: [
						["contact_id",vm.$store.contact.id]
					]
				}

				vm.getJSON("ContactLocation/get",params).then(response => {
					if (spinner)
						vm.spinner(false)

					if (response.success) {
						vm.locations = [
							{location_name: "Main",id:'main'},
							...response.data
							
						]
					}

					resolve(response)
				})
			})
		},
		fetchData() {
			this.spinner(true)

			let vm = this

			let promises = [
				this.fetchContact(this.$route.params.id,["User","Company",{model: "CustomerAssetCategory",fields:["id","name"]},{model:"ContactFile", fields:[]}]),
				this.fetchCategories(false)
			]

			Promise.all(promises).then(responses => {
				vm.spinner(false)

				if ("catSlug" in vm.$route.params) {
					//match either slug field or slugified name field
					let cat = responses[1].data.find(theCat => theCat.name.slugify() == vm.$route.params.catSlug || theCat.slug == vm.$route.params.catSlug)

					if (cat)
						vm.selectCat(cat)
				}

				if (responses[0].data.CustomerAssetCategory)
					vm.activeCategories = responses[0].data.CustomerAssetCategory.map(cat => cat.id)
				else
					vm.activeCategories = []

			})
		}
	},
	beforeRouteLeave(to,from,next) {
		//set back link
		if (this.setLastRoute)
			this.$store.lastRoute = from.fullPath

		next()
	},
	created() {
		this.fetchData()

	}
}
</script>